import React, { useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useGridFilterDisplay } from 'ag-grid-react';
import { Radio, Empty } from 'antd';
import { getLang } from 'shared/store/selectors/lang.selector';
import { ListItemsNavigator } from 'src/components/common/items-navigator';
import styles from './radio-set-filter.module.scss';
import { Button, Divider } from 'src/components/design-system';
import { colorPalette } from 'shared/constants';
import { useFocusTrap } from 'src/components/common/items-navigator';

/**
 * Filter logic for RadioSetFilter - used at column definition level
 * If matcher is provided, uses custom logic. Otherwise, does simple value equality.
 * @param {import('ag-grid-community').DoesFilterPassParams} params
 */
export const radioSetFilterDoesFilterPass = ({ model, node, handlerParams }) => {
  if (!model || model.value === undefined) {
    return true;
  }

  const cellValue = handlerParams.getValue(node);

  // Use custom matcher if provided, otherwise simple equality
  if (model.matcher) {
    return model.matcher(cellValue);
  }

  return cellValue === model.value;
};

/**
 * Custom AG Grid Radio Set Filter component for single-select filtering with predefined options
 * @param {import('ag-grid-react').CustomFilterDisplayProps & { options: Array<{ value: any, label: string }> }} props
 */
export function RadioSetFilter({ model, onModelChange, options = [] }) {
  const lang = useSelector(getLang('FILTERS'));
  const radioRef = useRef(null);
  const hidePopupRef = useRef(null);
  const triggerRef = useRef(null);

  const afterGuiAttached = useCallback(params => {
    hidePopupRef.current = params?.hidePopup ?? null;
    // Capture the element that opened the popup so we can restore focus when it closes
    triggerRef.current = document.activeElement;
    if (!params || !params.suppressFocus) {
      // Focus the first radio button for keyboard navigation.
      // Can't do this in an effect, as the component is not recreated when hidden and then shown again
      radioRef.current?.focus();
    }
  }, []);

  const afterGuiDetached = useCallback(() => {
    hidePopupRef.current = null;
    const trigger = triggerRef.current;
    triggerRef.current = null;
    // Restore focus to the trigger element only if AG Grid didn't already restore it
    requestAnimationFrame(() => {
      if (
        trigger &&
        document.contains(trigger) &&
        (!document.activeElement || document.activeElement === document.body)
      ) {
        trigger.focus();
      }
    });
  }, []);

  useGridFilterDisplay({
    afterGuiAttached,
    afterGuiDetached,
  });

  const onReset = useCallback(() => {
    onModelChange(null);
    hidePopupRef.current?.();
  }, [onModelChange]);

  const onChange = useCallback(
    e => {
      const value = e.target.value;
      const selectedOption = options.find(opt => opt.value === value);
      onModelChange({ value, matcher: selectedOption?.matcher });
    },
    [onModelChange, options],
  );

  const hasSelection = model?.value !== undefined;

  const { rootRef } = useFocusTrap();

  return (
    <div className={styles.root} ref={rootRef}>
      <div className={styles.header}>
        <Button
          size="small"
          onClick={onReset}
          disabled={!hasSelection}
          className={styles.buttonAction}>
          {lang.RESET}
        </Button>
      </div>

      <Divider dividerColor={colorPalette.neutral4} className={styles.divider} />

      {options.length > 0 ? (
        <Radio.Group value={model?.value} onChange={onChange}>
          <ListItemsNavigator className={styles.options} itemOverrides={{ role: 'radio' }}>
            {options.map((opt, index) => (
              <Radio
                key={opt.value}
                value={opt.value}
                className={styles.radio}
                ref={index === 0 ? radioRef : undefined}>
                {opt.label}
              </Radio>
            ))}
          </ListItemsNavigator>
        </Radio.Group>
      ) : (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={lang.NO_OPTIONS} />
      )}
    </div>
  );
}
